// client/src/api/socketService.js
import io from 'socket.io-client';
import gameService from './gameService';
import { readAuthToken } from '../utils/authToken';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.gameUpdateCallback = null;
    this.url = process.env.REACT_APP_SOCKET_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3099');
  }
  
  init() {
    if (this.socket) {
      console.log("[SOCKET] Socket déjà initialisé, aucune action nécessaire");
      return;
    }
    
    try {
      console.log(`[SOCKET] Tentative de connexion à ${this.url}`);
      // Authentifier la socket (JWT) — le serveur l'utilise pour envoyer à
      // chaque joueur l'état du plateau VU DE SON CÔTÉ en PvP.
      const authToken = readAuthToken();
      this.socket = io(this.url, {
        auth: authToken ? { token: authToken } : {},
      });
      
      this.socket.on('connect', () => {
        console.log('[SOCKET] Connecté au serveur');
        this.isConnected = true;
        // R34mm2 : après une reconnexion, la room serveur a été perdue —
        // sans re-join, plus aucun game-update n'arrive : le client reste
        // figé sur « tour de l'adversaire » pendant que le serveur continue
        // (auto-pass 30 s) → le joueur ne joue jamais.
        if (this.activeGameId) {
          console.log(`[SOCKET] Reconnexion — re-join de la partie ${this.activeGameId}`);
          this.socket.emit('join-game', this.activeGameId);
          try { window.dispatchEvent(new CustomEvent('elementum:socket-rejoined', { detail: { gameId: this.activeGameId } })); } catch (e) { /* SSR */ }
        }
      });
      
      this.socket.on('disconnect', () => {
        console.log('[SOCKET] Déconnecté du serveur');
        this.isConnected = false;
      });
      
      this.socket.on('error', (error) => {
        console.error('[SOCKET] Erreur:', error);
      });
      
      this.socket.on('game-update', (data) => {
        console.log('[SOCKET] Mise à jour de jeu reçue:', data);
        if (this.gameUpdateCallback) {
          this.gameUpdateCallback(data);
        }
      });
      // R30 : matchmaking classé — un adversaire a été trouvé
      this.socket.on('match-found', (data) => {
        console.log('[SOCKET] Adversaire trouvé:', data);
        if (this.matchFoundCallback) {
          this.matchFoundCallback(data);
        }
      });

      // R34g : les 2 joueurs ont accepté le duel
      this.socket.on('match-confirmed', (data) => {
        console.log('[SOCKET] Duel confirmé:', data);
        if (this.matchConfirmedCallback) this.matchConfirmedCallback(data);
      });

      // R34g : duel annulé
      this.socket.on('match-cancelled', (data) => {
        console.warn('[SOCKET] Duel annulé:', data);
        if (this.matchCancelledCallback) this.matchCancelledCallback(data);
      });

      // R30 : partie expirée pour inactivité (purge serveur 60 s)
      this.socket.on('game-expired', (data) => {
        console.warn('[SOCKET] Partie expirée:', data);
        if (this.gameExpiredCallback) {
          this.gameExpiredCallback(data);
        }
      });

      // R34f5 : salle d'attente — un adversaire a rejoint la partie créée
      this.socket.on('player-joined', (data) => {
        console.log('[SOCKET] Joueur rejoint:', data);
        if (this.playerJoinedCallbacks) {
          this.playerJoinedCallbacks.forEach((cb) => cb(data));
        }
      });

      // R34cc : défi de revanche + confirmation/annulation → événements
      // window globaux (fonctionne depuis n'importe quelle page, pas juste
      // le matchmaking — ex: écran de fin de partie).
      this.socket.on('rematch-challenge', (data) => {
        console.log('[SOCKET] Défi de revanche reçu:', data);
        try { window.dispatchEvent(new CustomEvent('elementum:rematch-challenge', { detail: data })); } catch (e) { /* SSR */ }
      });
      this.socket.on('match-confirmed', (data) => {
        try { window.dispatchEvent(new CustomEvent('elementum:match-confirmed', { detail: data })); } catch (e) { /* SSR */ }
      });
      this.socket.on('match-cancelled', (data) => {
        try { window.dispatchEvent(new CustomEvent('elementum:match-cancelled', { detail: data })); } catch (e) { /* SSR */ }
      });
    } catch (error) {
      console.error('[SOCKET] Erreur lors de l\'initialisation:', error);
    }
  }
  
  // R30 : callbacks matchmaking / expiration
  onMatchFound(callback) {
    this.matchFoundCallback = callback;
  }

  // R34g : duel confirmé par les 2 joueurs → démarrage
  onMatchConfirmed(callback) {
    this.matchConfirmedCallback = callback;
  }

  // R34g : duel annulé (refus, délai dépassé, mise impossible)
  onMatchCancelled(callback) {
    this.matchCancelledCallback = callback;
  }

  onGameExpired(callback) {
    this.gameExpiredCallback = callback;
  }

  // R34f5 : abonnement aux arrivées d'adversaire (salle d'attente). Retourne un désabonnement.
  onPlayerJoined(callback) {
    if (!this.playerJoinedCallbacks) this.playerJoinedCallbacks = [];
    this.playerJoinedCallbacks.push(callback);
    return () => {
      this.playerJoinedCallbacks = this.playerJoinedCallbacks.filter((cb) => cb !== callback);
    };
  }

  joinGame(gameId) {
    // R34mm2 : mémoriser la partie active pour la rejouer (re-join) après
    // une reconnexion socket (le tunnel Cloudflare coupe souvent le WS).
    this.activeGameId = gameId;
    if (!this.socket) {
      console.warn('[SOCKET] Tentative de rejoindre une partie sans socket initialisé');
      return;
    }

    const doJoin = () => {
      console.log(`[SOCKET] Rejoindre la partie ${gameId}`);
      this.socket.emit('join-game', gameId);
    };

    // Si pas encore connecté, attendre l'événement connect (une seule fois)
    if (this.isConnected) {
      doJoin();
    } else {
      this.socket.once('connect', doJoin);
    }
  }
  
  leaveGame(gameId, options = {}) {
    if (!this.socket) {
      console.warn('[SOCKET] Tentative de quitter une partie sans socket initialisé');
      return;
    }
    
    if (this.activeGameId === gameId) this.activeGameId = null;
    console.log(`[SOCKET] Quitter la partie ${gameId}`);
    if (this.isConnected) {
      this.socket.emit('leave-game', gameId);
    }
    
    // Ne pas appeler surrenderGame si l'option skipSurrender est true
    if (!options.skipSurrender) {
      console.log(`[SOCKET] Option skipSurrender non spécifiée, abandon de la partie ${gameId}`);
      // eslint-disable-next-line no-unused-vars
      gameService.surrenderGame(gameId, { skipServerCall: false }).catch(err => 
        console.warn('[SOCKET] Erreur lors de l\'abandon:', err)
      );
    } else {
      console.log(`[SOCKET] Option skipSurrender spécifiée, pas d'abandon automatique pour ${gameId}`);
    }
  }
  
  onGameUpdate(callback) {
    this.gameUpdateCallback = callback;
    
    return () => {
      this.gameUpdateCallback = null;
    };
  }
  
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
      this.socket = null;
    }
  }
}

const socketService = new SocketService();
export default socketService;